import * as React from 'react'

type LinkClickHandler<ELEMENT extends HTMLElement = HTMLElement> = {
  navigate: (event: React.MouseEvent<ELEMENT>) => void
  onClick?: (event: React.MouseEvent<ELEMENT>) => void
}

export function useLinkClickHandler<ELEMENT extends HTMLElement = HTMLElement>(
  handler: LinkClickHandler<ELEMENT>,
): (event: React.MouseEvent<ELEMENT>) => void {
  return React.useCallback(makeLinkClickHandler(handler), [handler.navigate, handler.onClick])
}

export function makeLinkClickHandler<ELEMENT extends HTMLElement = HTMLElement>({
  navigate,
  onClick,
}: LinkClickHandler<ELEMENT>): (event: React.MouseEvent<ELEMENT>) => void {
  return (event: React.MouseEvent<ELEMENT>) => {
    onClick && onClick(event)
    if (event.defaultPrevented) {
      return
    }

    if (shouldProcessMouseEvent(event)) {
      event.preventDefault()
      navigate(event)
    }
  }
}

function shouldProcessMouseEvent<ELEMENT extends HTMLElement>(event: React.MouseEvent<ELEMENT>): boolean {
  // do not handle link with target different than _self (_blank, etc ...)
  const target = event.currentTarget instanceof HTMLAnchorElement ? event.currentTarget.target : undefined
  if (target && target !== '_self') {
    return false
  }

  // do not handle non-main button click (right click, middle click, etc ...)
  if (event.button !== 0) {
    return false
  }

  // do not handle modified click (click+meta, click+ctrl, etc ...)
  if (event.ctrlKey || event.metaKey || event.ctrlKey || event.shiftKey) {
    return false
  }

  // everything else goes
  return true
}
